import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { Platform, useWindowDimensions } from "react-native";

type Orientation = "portrait" | "landscape";

export interface PlatformContextValue {
  platform: "web" | "ios" | "android";

  isWeb: boolean;
  isIOS: boolean;
  isAndroid: boolean;
  isNativeApp: boolean;

  isPhone: boolean;
  isTablet: boolean;
  isIPad: boolean;

  isBrowser: boolean;
  isMobileBrowser: boolean;
  isIOSBrowser: boolean;
  isDesktopBrowser: boolean;
  isPWA: boolean;
  isInAppWebView: boolean;

  orientation: Orientation;
  showWebVersion: boolean;
}

export const PlatformContext = createContext<PlatformContextValue | null>(null);

const WEB_DESKTOP_BREAKPOINT = 1024;

export const PlatformProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { width, height } = useWindowDimensions();

  // 🔑 Force re-render on WEB resize
  const [windowSize, setWindowSize] = useState(() => ({
    width,
    height,
  }));

  const isWeb = Platform.OS === "web";

  useEffect(() => {
    if (!isWeb || typeof window === "undefined") return;

    const onResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [isWeb]);

  const effectiveWidth = isWeb ? windowSize.width : width;
  const effectiveHeight = isWeb ? windowSize.height : height;

  const value = useMemo<PlatformContextValue>(() => {
    const platform = Platform.OS as "web" | "ios" | "android";

    const isIOS = platform === "ios";
    const isAndroid = platform === "android";
    const isNativeApp = !isWeb;

    const orientation: Orientation =
      effectiveWidth > effectiveHeight ? "landscape" : "portrait";

    const minDim = Math.min(effectiveWidth, effectiveHeight);

    const userAgent =
      typeof navigator !== "undefined" ? navigator.userAgent : "";

    const isPWA =
      isWeb &&
      typeof window !== "undefined" &&
      window.matchMedia("(display-mode: standalone)").matches;

    const isInAppWebView =
      isWeb &&
      /FBAN|FBAV|Instagram|Line|Twitter|LinkedIn/i.test(userAgent);

    const isBrowser = isWeb && !isPWA && !isInAppWebView;

    const isMobileBrowser =
      isBrowser && /Android|iPhone|iPad|iPod/i.test(userAgent);
    const isIOSBrowser = /iPhone|iPad|iPod/i.test(userAgent);
    const isDesktopBrowser = isBrowser && !isMobileBrowser;

    const isIPad =
      isIOS ||
      (isWeb && /iPad/i.test(userAgent)) ||
      (isWeb && /Macintosh/i.test(userAgent) && "ontouchend" in window);

    const isTablet =
      isIPad || (minDim >= 600 && minDim < 1024);

    const isPhone = !isTablet;

    const showWebVersion =
      isWeb &&
      (effectiveWidth >= WEB_DESKTOP_BREAKPOINT ||
        ((isIPad || isTablet) && orientation === "landscape"));

    return {
      platform,
      isWeb,
      isIOS,
      isAndroid,
      isNativeApp,

      isPhone,
      isTablet,
      isIPad,

      isBrowser,
      isMobileBrowser,
      isIOSBrowser,
      isDesktopBrowser,
      isPWA,
      isInAppWebView,

      orientation,
      showWebVersion,
    };
  }, [effectiveWidth, effectiveHeight, isWeb]);

  return (
    <PlatformContext.Provider value={value}>
      {children}
    </PlatformContext.Provider>
  );
};


export const usePlatform = () => {
  const ctx = useContext(PlatformContext);
  if (!ctx) {
    throw new Error("usePlatform must be used inside PlatformProvider");
  }
  return ctx;
};
